import mustache from "mustache";
import { ISaveService } from "../../../abstractions/services/actions/ISaveService";
import { BaseRequest } from "../../../base/request/BaseRequest";
import { BaseResponse } from "../../../base/response/BaseResponse";
import { serviceProvider } from "../../../comun/provider/ServiceProvider";
import { IFormBodyRender } from "../../definitions/form/IFormBodyRender";
import { BaseCreateRender } from "../BaseCreateRender";
import template from "./fsFormTemplate.html";

mustache.parse(template);

/** Base de modales de formulario a pantalla completa (auto-montados sobre `body`,
 *  extiende `BaseCreateRender`): barra superior (atrás + título/subtítulo), cuerpo
 *  scrollable con el form y footer fijo con Cancelar/Guardar. Al pulsar Guardar
 *  valida el form body y, si es válido, guarda con `saveService`, cierra y recarga
 *  la página. La subclase define `elementId`, `title`, `getFormBody` y `saveService`. */
export abstract class BaseFullScreenFormModalRender<
  TRequest extends BaseRequest,
  TResponse extends BaseResponse,
> extends BaseCreateRender {
  /** Request que se guarda al pulsar Guardar. */
  protected readonly _request: TRequest;

  /** @param request Request a editar/crear (lo mutan los campos del form body). */
  constructor(request: TRequest) {
    super();
    this._request = request;
  }

  /** Título de la barra superior. */
  protected abstract get title(): string;

  /** Subtítulo opcional (p. ej. nombre de la finca). */
  protected get subtitle(): string | undefined {
    return undefined;
  }

  /** Texto del botón guardar. */
  protected get saveLabel(): string {
    return "Guardar";
  }

  /** Icono Phosphor del botón guardar (o null). */
  protected get saveIcon(): string | null {
    return null;
  }

  /** Cuerpo del formulario (render/connect/dispose + validate). */
  protected abstract getFormBody(): IFormBodyRender;

  /** Contenido opcional a la izquierda del footer (p. ej. badges de estado).
   *  Se llama después de `getFormBody()`. Vacío = sin zona izquierda. */
  protected getFooterLeft(): string {
    return "";
  }

  /** Servicio de guardado del request. */
  protected abstract get saveService(): ISaveService<TRequest, TResponse>;

  /** Acción tras un guardado exitoso. Por defecto cierra el modal y recarga la
   *  página (refresca la lista/página actual). Las subclases pueden sobrescribirla
   *  para encadenar otro flujo (p. ej. cerrar y abrir otro modal con la respuesta). */
  protected onSaveSuccess(_response: TResponse): void {
    this.dispose();
    serviceProvider.getLayoutService().reload();
  }

  /** HTML: overlay full-screen + chrome (barra + form + footer). */
  protected getHtml(): string {
    this._formBody = this.getFormBody();
    return mustache.render(template, {
      title: this.title,
      subtitle: this.subtitle,
      cancelLabel: "Cancelar",
      saveLabel: this.saveLabel,
      saveIcon: this.saveIcon,
      content: this._formBody.render(),
      footerLeft: this.getFooterLeft(),
    });
  }

  /** Conecta el form, cablea barra/cancelar/guardar y anima la apertura. */
  protected afterInit(element: HTMLDivElement): void {
    this._formBody?.connect();
    this._overlay = element.querySelector<HTMLDivElement>(".sx-modal-overlay");
    this._closeBtn = element.querySelector<HTMLElement>('[data-act="close"]');
    this._cancelBtn = element.querySelector<HTMLElement>('[data-act="cancel"]');
    this._saveBtn = element.querySelector<HTMLButtonElement>('[data-act="save"]');
    this._closeBtn?.addEventListener("click", this._onClose);
    this._cancelBtn?.addEventListener("click", this._onClose);
    this._saveBtn?.addEventListener("click", this._onSave);
    requestAnimationFrame(() =>
      this._overlay?.classList.add("sx-modal-overlay--open"),
    );
  }

  /** Libera listeners y dispone el form body. */
  protected beforeDispose(_element: HTMLDivElement): void {
    this._closeBtn?.removeEventListener("click", this._onClose);
    this._cancelBtn?.removeEventListener("click", this._onClose);
    this._saveBtn?.removeEventListener("click", this._onSave);
    this._formBody?.dispose();
  }

  private _formBody: IFormBodyRender | null = null;
  private _overlay: HTMLDivElement | null = null;
  private _closeBtn: HTMLElement | null = null;
  private _cancelBtn: HTMLElement | null = null;
  private _saveBtn: HTMLButtonElement | null = null;

  private _onClose = (): void => this.dispose();

  /** Valida y guarda; al éxito cierra y recarga la página. */
  private _onSave = (): void => {
    if (!this._formBody?.validate() || !this._saveBtn) return;

    this._saveBtn.disabled = true;
    this.saveService
      .saveAsync(this._request)
      .then((response) => this.onSaveSuccess(response))
      .catch((error) => {
        serviceProvider.getErrorService().addError("Error al guardar", error);
      })
      .finally(() => {
        if (this._saveBtn) this._saveBtn.disabled = false;
      });
  };
}
